/**
 * Agent Builder — Build view (Phase 2, T2.4).
 *
 * The visual compose surface: a @xyflow/react canvas (role palette + node
 * inspector) over an editable WorkflowGraph, plus validate + publish against the
 * T2.2 endpoints. Ported from Agent Studio's views/BuildView.tsx, restyled for
 * factory-ui and wired to agentBuilderApi.
 *
 * The cockpit appears ONLY when VITE_ENABLE_AGENT_BUILDER is on (the route is
 * registered behind that flag in App.tsx). Additive: reuses the existing
 * @xyflow/react foundation (same v12 the KG-design wizard uses).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Background,
  BackgroundVariant,
  Controls,
  MiniMap,
  ReactFlow,
  ReactFlowProvider,
  useEdgesState,
  useNodesState,
  type Connection,
  type Edge,
  type EdgeChange,
  type Node,
  type NodeMouseHandler,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { CheckCircle2, AlertTriangle, UploadCloud, Plus, Trash2, Loader2, FilePlus2 } from 'lucide-react';
import { toast } from 'sonner';
import Button from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import { agentNodeTypes, type AgentFlowNodeData } from '../../components/agent-builder/AgentFlowNode';
import { toFlowEdges, toFlowNodes, graphOf } from '../../components/agent-builder/adapt';
import CodePanel from '../../components/agent-builder/CodePanel';
import { generateCode } from '../../components/agent-builder/codegen';
import { TIER_LABEL, TIER_MODEL } from '../../components/agent-builder/llmTiers';
import { agentBuilderApi } from '../../lib/api/agentBuilderApi';
import type {
  AgentRole,
  AgentWorkflow,
  GuardrailDef,
  LlmTier,
  OrchestrationStrategy,
  ValidationResult,
  WorkflowGraph,
  WorkflowNode,
} from '../../types/agentBuilder';

const ROLE_PALETTE: { role: AgentRole; kind: WorkflowNode['kind']; label: string }[] = [
  { role: 'orchestrator', kind: 'orchestrator', label: 'Orchestrator' },
  { role: 'retriever', kind: 'agent', label: 'Retriever' },
  { role: 'extractor', kind: 'agent', label: 'Extractor' },
  { role: 'analyzer', kind: 'agent', label: 'Analyzer' },
  { role: 'specialist', kind: 'agent', label: 'Specialist' },
  { role: 'strategist', kind: 'agent', label: 'Strategist' },
  { role: 'validator', kind: 'validator', label: 'Validator' },
  { role: 'critic', kind: 'guardrail', label: 'Critic / Audit' },
  { role: 'output', kind: 'output', label: 'Output' },
];

const EMPTY_GRAPH: WorkflowGraph = {
  id: 'wf-new',
  name: 'New Workflow',
  product: '',
  description: 'A new agent workflow.',
  strategy: 'sequential',
  nodes: [],
  edges: [],
};

/** Sentinel the cockpit passes as `workflowId` to request a blank canvas. */
export const NEW_WORKFLOW_SENTINEL = '__new__';

/** A fresh, collision-free workflow id for a brand-new draft. */
const freshWorkflowId = () => `wf-${Date.now().toString(36)}`;

/** A blank authoring graph with a unique id (avoids the constant `wf-new` clash). */
const blankGraph = (): WorkflowGraph => ({ ...EMPTY_GRAPH, id: freshWorkflowId(), nodes: [], edges: [] });

export interface BuildViewProps {
  /** Optional initial workflow id to load (deep-linked from the Library). */
  workflowId?: string;
  currentUser?: string;
}

export const BuildView: React.FC<BuildViewProps> = ({ workflowId, currentUser }) => {
  const [workflows, setWorkflows] = useState<AgentWorkflow[]>([]);
  const [guardrails, setGuardrails] = useState<GuardrailDef[]>([]);
  const [graph, setGraph] = useState<WorkflowGraph>(EMPTY_GRAPH);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [validation, setValidation] = useState<ValidationResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  // Right panel: node Inspector vs. the framework-free Python codegen.
  const [rightTab, setRightTab] = useState<'inspect' | 'code'>('inspect');

  const queryClient = useQueryClient();

  useEffect(() => {
    const blank = workflowId === NEW_WORKFLOW_SENTINEL;
    if (blank) setGraph(blankGraph()); // start a fresh draft immediately
    // Read through the shared React Query cache (staleTime: Infinity) so the
    // workflow/guardrail lists are reused across the Agent Builder views and
    // aren't re-fetched on revisit.
    queryClient
      .fetchQuery({ queryKey: ['agent-workflows'], queryFn: () => agentBuilderApi.listWorkflows(), staleTime: Infinity })
      .then((rows) => {
        setWorkflows(rows);
        if (blank) return; // keep the blank canvas; don't fall back to rows[0]
        const initial = workflowId ? rows.find((w) => w.workflowId === workflowId) : rows[0];
        if (initial) setGraph(graphOf(initial));
      })
      .catch(() => toast.error('Failed to load workflows'));
    queryClient
      .fetchQuery({ queryKey: ['agent-guardrails'], queryFn: () => agentBuilderApi.listGuardrails(), staleTime: Infinity })
      .then(setGuardrails)
      .catch(() => undefined);
  }, [workflowId, queryClient]);

  // React Flow owns the LIVE canvas state (so measurement/`dimensions` changes are
  // retained — required for handle bounds, i.e. drag-to-connect). `graph` stays the
  // persistence source of truth; we sync graph -> RF below.
  const [rfNodes, setRfNodes, onNodesChange] = useNodesState<Node<AgentFlowNodeData>>([]);
  const [rfEdges, setRfEdges, onEdgesChange] = useEdgesState<Edge>([]);
  const selected = graph.nodes.find((n) => n.id === selectedId) ?? null;
  // Generated only while the Generate-Python tab is open (memoized on the graph).
  const files = useMemo(
    () => (rightTab === 'code' ? generateCode(graph, guardrails) : []),
    [rightTab, graph, guardrails],
  );

  // A signature of everything that affects RENDERING except positions, so a
  // position drag never reseeds the canvas (which would wipe measured dimensions
  // and re-break connecting).
  const structureSig = useMemo(
    () =>
      JSON.stringify({
        id: graph.id,
        nodes: graph.nodes.map((n) => ({
          i: n.id, k: n.kind, r: n.role, l: n.label, t: n.llmTier,
          g: n.guardrails ?? [], h: !!n.requiresHitl, rt: n.refineTo ?? null, mi: n.maxIterations ?? null,
        })),
        edges: graph.edges.map((e) => ({ i: e.id, s: e.source, t: e.target, f: !!e.feedback, l: e.label ?? null })),
      }),
    [graph],
  );

  // Sync graph -> React Flow on structural / selection change, MERGING the live
  // position + measured size from the previous nodes so measurement survives.
  useEffect(() => {
    setRfNodes((prev) => {
      const old = new Map(prev.map((n) => [n.id, n]));
      return toFlowNodes(graph, { selectedId }).map((n) => {
        const o = old.get(n.id);
        return o
          ? { ...n, position: o.position, measured: o.measured, width: o.width, height: o.height }
          : n;
      });
    });
    setRfEdges(toFlowEdges(graph));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [structureSig, selectedId]);

  const onNodeClick: NodeMouseHandler = (_, node) => {
    setSelectedId(node.id);
    setRightTab('inspect'); // selecting a node always surfaces the Inspector (Agent Studio parity)
  };

  // Persist the final dragged position back to `graph` (for Save / codegen). The
  // structure signature excludes positions, so this never triggers a reseed.
  const onNodeDragStop = useCallback((_e: unknown, node: { id: string; position: { x: number; y: number } }) => {
    setGraph((g) => ({
      ...g,
      nodes: g.nodes.map((n) => (n.id === node.id ? { ...n, x: node.position.x, y: node.position.y } : n)),
    }));
  }, []);

  // Drag from a node's right (source) dot to another node's left (target) dot to
  // wire them. Skips self-loops and duplicate source->target edges. The reseed
  // effect mirrors the new edge into `rfEdges`.
  const onConnect = (c: Connection) => {
    if (!c.source || !c.target || c.source === c.target) return;
    setGraph((g) => {
      if (g.edges.some((e) => e.source === c.source && e.target === c.target)) return g;
      const edge = { id: `e-${c.source}->${c.target}`, source: c.source!, target: c.target! };
      return { ...g, edges: [...g.edges, edge] };
    });
  };

  // Apply select/remove to the live edges, then mirror `remove` into `graph` so a
  // selected edge + Delete/Backspace permanently drops the wire.
  const handleEdgesChange = (changes: EdgeChange[]) => {
    onEdgesChange(changes);
    const removed = changes.filter((c) => c.type === 'remove').map((c) => (c as { id: string }).id);
    if (removed.length > 0) {
      setGraph((g) => ({ ...g, edges: g.edges.filter((e) => !removed.includes(e.id)) }));
    }
  };

  const updateNode = useCallback((id: string, patch: Partial<WorkflowNode>) => {
    setGraph((g) => ({ ...g, nodes: g.nodes.map((n) => (n.id === id ? { ...n, ...patch } : n)) }));
  }, []);

  const deleteNode = useCallback((id: string) => {
    setGraph((g) => ({
      ...g,
      nodes: g.nodes.filter((n) => n.id !== id),
      edges: g.edges.filter((e) => e.source !== id && e.target !== id),
    }));
    setSelectedId(null);
  }, []);

  const addNode = (p: (typeof ROLE_PALETTE)[number]) => {
    const id = `n-${p.role}-${Math.floor(performance.now())}`;
    const node: WorkflowNode = {
      id,
      kind: p.kind,
      label: p.label,
      role: p.role,
      llmTier: p.kind === 'output' ? null : 'mid',
      guardrails: [],
      inputs: [],
      outputs: [],
      x: 120 + (graph.nodes.length % 4) * 60,
      y: 120 + (graph.nodes.length % 5) * 40,
    };
    setGraph((g) => ({ ...g, nodes: [...g.nodes, node] }));
    setSelectedId(id);
  };

  const onSelectWorkflow = (id: string) => {
    const wf = workflows.find((w) => w.workflowId === id);
    if (wf) {
      setGraph(graphOf(wf));
      setSelectedId(null);
      setValidation(null);
    }
  };

  /** Reset the canvas to a blank draft with a fresh, unique id. `Save draft`
   *  then persists it via createWorkflow (no clash with existing ids). */
  const handleNewWorkflow = () => {
    setGraph(blankGraph());
    setSelectedId(null);
    setValidation(null);
    setRightTab('inspect');
  };

  const isSaved = workflows.some((w) => w.workflowId === graph.id);

  /** Permanently delete the current workflow (+ its versions), then reset to a
   *  blank draft. Run history is kept server-side. */
  const handleDeleteWorkflow = async () => {
    setBusy(true);
    try {
      await agentBuilderApi.deleteWorkflow(graph.id, currentUser);
      setWorkflows((prev) => prev.filter((w) => w.workflowId !== graph.id));
      queryClient.invalidateQueries({ queryKey: ['agent-workflows'] });
      toast.success('Workflow deleted');
      setDeleteOpen(false);
      handleNewWorkflow();
    } catch {
      toast.error('Failed to delete workflow');
    } finally {
      setBusy(false);
    }
  };

  const asWorkflow = (): AgentWorkflow => ({
    workflowId: graph.id,
    name: graph.name,
    product: graph.product,
    strategy: graph.strategy,
    status: 'draft',
    graphJson: graph,
    headVersion: 0,
  });

  const handleSave = async () => {
    setBusy(true);
    try {
      const exists = workflows.some((w) => w.workflowId === graph.id);
      const wf = exists
        ? await agentBuilderApi.updateWorkflow(graph.id, asWorkflow(), currentUser)
        : await agentBuilderApi.createWorkflow(asWorkflow(), currentUser);
      setWorkflows((prev) => {
        const others = prev.filter((w) => w.workflowId !== wf.workflowId);
        return [wf, ...others];
      });
      queryClient.invalidateQueries({ queryKey: ['agent-workflows'] });
      toast.success('Workflow saved');
    } catch {
      toast.error('Failed to save workflow');
    } finally {
      setBusy(false);
    }
  };

  const handleValidate = async () => {
    setBusy(true);
    try {
      await agentBuilderApi.createWorkflow(asWorkflow(), currentUser); // ensure persisted
      const res = await agentBuilderApi.validateWorkflow(graph.id);
      setValidation(res);
      if (res.valid) toast.success('Workflow is valid');
      else toast.error(`${res.violations.length} validation issue(s)`);
    } catch {
      toast.error('Validation failed');
    } finally {
      setBusy(false);
    }
  };

  const handlePublish = async () => {
    setBusy(true);
    try {
      await agentBuilderApi.createWorkflow(asWorkflow(), currentUser);
      const version = await agentBuilderApi.publishWorkflow(graph.id, currentUser);
      queryClient.invalidateQueries({ queryKey: ['agent-workflows'] });
      toast.success(`Published version ${version.version}`);
      setValidation({ valid: true, violations: [], requiredExternalInputs: version.requiredExternalInputs });
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.violations) {
        setValidation({ valid: false, violations: detail.violations, requiredExternalInputs: [] });
      }
      toast.error('Publish failed (workflow may be invalid)');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex h-full overflow-hidden" data-testid="agent-build-view">
      {/* Left palette: scrollable body + pinned action footer */}
      <div className="flex w-[300px] shrink-0 flex-col border-r border-neutral-200 bg-neutral-50">
        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4">
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <label className="block text-[11px] uppercase tracking-wide text-neutral-500">Workflow</label>
              <button
                type="button"
                onClick={handleNewWorkflow}
                className="inline-flex items-center gap-1 rounded-md border border-neutral-200 bg-white px-1.5 py-0.5 text-[10px] font-medium text-secondary-700 transition-colors hover:border-secondary-400"
                aria-label="New workflow"
              >
                <FilePlus2 className="h-3 w-3" /> New
              </button>
            </div>
            <select
              value={workflows.some((w) => w.workflowId === graph.id) ? graph.id : ''}
              onChange={(e) => onSelectWorkflow(e.target.value)}
              className="w-full rounded-lg border border-neutral-300 px-2.5 py-2 text-[13px]"
              aria-label="Switch workflow"
            >
              {!workflows.some((w) => w.workflowId === graph.id) && <option value="">{graph.name} (editing)</option>}
              {workflows.map((w) => (
                <option key={w.workflowId} value={w.workflowId}>{w.name}</option>
              ))}
            </select>

            <div>
              <label className="mb-1 block text-[10px] uppercase tracking-wide text-neutral-400">Name</label>
              <input
                value={graph.name}
                onChange={(e) => setGraph((g) => ({ ...g, name: e.target.value }))}
                placeholder="Workflow name"
                className="w-full rounded-lg border border-neutral-300 px-2.5 py-1.5 text-[12px]"
                aria-label="Workflow name"
              />
            </div>

            <div>
              <label className="mb-1 block text-[10px] uppercase tracking-wide text-neutral-400">Description</label>
              <textarea
                value={graph.description ?? ''}
                onChange={(e) => setGraph((g) => ({ ...g, description: e.target.value }))}
                rows={2}
                placeholder="What does this workflow do?"
                className="w-full resize-none rounded-lg border border-neutral-300 px-2.5 py-1.5 text-[11px] leading-relaxed"
                aria-label="Workflow description"
              />
            </div>

            <div>
              <label className="mb-1 block text-[10px] uppercase tracking-wide text-neutral-400">Strategy</label>
              <select
                value={graph.strategy}
                onChange={(e) => setGraph((g) => ({ ...g, strategy: e.target.value as OrchestrationStrategy }))}
                className="w-full rounded-lg border border-neutral-300 px-2.5 py-1.5 text-[12px]"
                aria-label="Orchestration strategy"
              >
                <option value="sequential">sequential</option>
                <option value="dispatch-waves">dispatch-waves</option>
                <option value="supervisor-routing">supervisor-routing</option>
              </select>
            </div>
          </div>

          <div>
            <label className="mb-2 block text-[11px] uppercase tracking-wide text-neutral-500">Add node</label>
            <div className="grid grid-cols-2 gap-1.5">
              {ROLE_PALETTE.map((p) => (
                <button
                  key={p.role}
                  onClick={() => addNode(p)}
                  className="rounded-lg border border-neutral-200 bg-white px-2 py-2 text-left text-[11px] text-neutral-700 transition-colors hover:border-secondary-400"
                >
                  <Plus className="mr-0.5 inline h-3 w-3" /> {p.label}
                </button>
              ))}
            </div>
            <p className="mt-2 text-[10px] leading-relaxed text-neutral-500">
              Drag from a node's right dot to another node's left dot to connect them. Select an edge and
              press Delete to remove it.
            </p>
          </div>
        </div>

        <div className="flex shrink-0 flex-col gap-2 border-t border-neutral-200 p-4">
          <Button variant="outline" size="sm" onClick={handleSave} disabled={busy}>Save draft</Button>
          <Button variant="secondary" size="sm" onClick={handleValidate} disabled={busy}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Validate
          </Button>
          <Button variant="primary" size="sm" onClick={handlePublish} disabled={busy}>
            <UploadCloud className="h-4 w-4" /> Publish
          </Button>
          {isSaved && (
            <button
              type="button"
              onClick={() => setDeleteOpen(true)}
              disabled={busy}
              className="mt-1 flex items-center justify-center gap-1 rounded-lg border border-red-200 bg-red-50 px-3 py-1.5 text-[12px] text-red-600 transition-colors hover:bg-red-100 disabled:opacity-50"
            >
              <Trash2 className="h-3.5 w-3.5" /> Delete workflow
            </button>
          )}
          {validation && (
            <div
              data-testid="validation-result"
              className={`rounded-lg border p-2 text-[11px] ${
                validation.valid ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-red-200 bg-red-50 text-red-700'
              }`}
            >
              {validation.valid ? (
                <span className="flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> Valid</span>
              ) : (
                <ul className="space-y-1">
                  {validation.violations.map((v, i) => (
                    <li key={i} className="flex items-start gap-1"><AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />{v}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Canvas */}
      <div className="relative min-w-0 flex-1">
        <ReactFlowProvider>
          <ReactFlow
            nodes={rfNodes}
            edges={rfEdges}
            nodeTypes={agentNodeTypes}
            onNodeClick={onNodeClick}
            onNodesChange={onNodesChange}
            onNodeDragStop={onNodeDragStop}
            onConnect={onConnect}
            onEdgesChange={handleEdgesChange}
            onPaneClick={() => setSelectedId(null)}
            fitView
            fitViewOptions={{ padding: 0.25 }}
            proOptions={{ hideAttribution: true }}
            minZoom={0.4}
          >
            <Background variant={BackgroundVariant.Dots} gap={22} size={1} color="#e2e8f0" />
            <Controls showInteractive={false} />
            <MiniMap pannable nodeColor="#cbd5e1" />
          </ReactFlow>
        </ReactFlowProvider>
      </div>

      {/* Right panel: Inspector / Generate Python tabs (Agent Studio parity) */}
      <div className="flex w-[360px] shrink-0 flex-col border-l border-neutral-200 bg-neutral-50">
        <div className="flex shrink-0 border-b border-neutral-200" role="tablist" aria-label="Build inspector">
          {(['inspect', 'code'] as const).map((t) => (
            <button
              key={t}
              role="tab"
              aria-selected={rightTab === t}
              onClick={() => setRightTab(t)}
              className={`flex-1 px-4 py-3 text-[13px] font-medium transition-colors ${
                rightTab === t
                  ? 'border-b-2 border-secondary-500 text-secondary-700'
                  : 'text-neutral-500 hover:text-neutral-800'
              }`}
            >
              {t === 'inspect' ? 'Inspector' : 'Generate Python'}
            </button>
          ))}
        </div>
        <div className="min-h-0 flex-1">
          {rightTab === 'inspect' ? (
            <Inspector
              key={selected?.id ?? 'none'}
              node={selected}
              guardrails={guardrails}
              onChange={(patch) => selected && updateNode(selected.id, patch)}
              onDelete={() => selected && deleteNode(selected.id)}
            />
          ) : (
            <CodePanel files={files} />
          )}
        </div>
      </div>

      <ConfirmDialog
        isOpen={deleteOpen}
        title="Delete workflow?"
        message={`Permanently delete "${graph.name}"? Published versions are removed; run history is preserved. This can't be undone.`}
        confirmLabel="Delete"
        variant="danger"
        loading={busy}
        onCancel={() => (busy ? undefined : setDeleteOpen(false))}
        onConfirm={handleDeleteWorkflow}
      />
    </div>
  );
};

function Inspector({
  node,
  guardrails,
  onChange,
  onDelete,
}: {
  node: WorkflowNode | null;
  guardrails: GuardrailDef[];
  onChange: (patch: Partial<WorkflowNode>) => void;
  onDelete: () => void;
}) {
  if (!node) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 p-8 text-center text-neutral-500">
        <span className="text-3xl opacity-40">◈</span>
        <p className="text-[13px]">Select a node on the canvas to configure it.</p>
      </div>
    );
  }
  const tiers: LlmTier[] = ['premium', 'mid', 'cost'];
  const ROLES: AgentRole[] = [
    'orchestrator', 'preprocessor', 'retriever', 'extractor', 'analyzer', 'specialist',
    'researcher', 'strategist', 'validator', 'refiner', 'critic', 'output',
  ];

  return (
    <div className="flex h-full flex-col overflow-y-auto p-4" data-testid="node-inspector">
      <Field label="Label">
        <input
          value={node.label}
          onChange={(e) => onChange({ label: e.target.value })}
          className="w-full rounded-lg border border-neutral-300 px-2.5 py-2 text-[13px]"
          aria-label="Node label"
        />
      </Field>

      <Field label="Role">
        <select
          value={node.role}
          onChange={(e) => onChange({ role: e.target.value as AgentRole })}
          className="w-full rounded-lg border border-neutral-300 px-2.5 py-2 text-[13px]"
          aria-label="Node role"
        >
          {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
      </Field>

      {node.kind !== 'output' && (
        <Field label="LLM tier">
          <div className="flex gap-1.5">
            {tiers.map((t) => (
              <button
                key={t}
                onClick={() => onChange({ llmTier: t })}
                title={`${TIER_LABEL[t]} · ${TIER_MODEL[t]}`}
                className={`flex-1 rounded-lg border px-2 py-1.5 text-[11px] capitalize transition-colors ${
                  node.llmTier === t ? 'border-secondary-500 bg-secondary-50 text-secondary-700' : 'border-neutral-300 bg-white text-neutral-600'
                }`}
              >
                {t}
              </button>
            ))}
          </div>
          {node.llmTier && (
            <p className="mt-1.5 text-[11px] text-neutral-500" data-testid="tier-model">
              Model: {TIER_LABEL[node.llmTier]} ·{' '}
              <span className="font-mono text-neutral-600">{TIER_MODEL[node.llmTier]}</span>
            </p>
          )}
        </Field>
      )}

      <Field label="Objective / system prompt">
        <textarea
          value={node.objective ?? ''}
          onChange={(e) => onChange({ objective: e.target.value })}
          rows={4}
          placeholder="What is this agent asked to do?"
          className="w-full resize-none rounded-lg border border-neutral-300 px-2.5 py-2 text-[12px]"
          aria-label="Node objective"
        />
      </Field>

      <Field label="Guardrails">
        <div className="flex flex-col gap-1">
          {guardrails.map((g) => {
            const on = (node.guardrails ?? []).includes(g.guardrailId);
            return (
              <button
                key={g.guardrailId}
                onClick={() =>
                  onChange({
                    guardrails: on
                      ? (node.guardrails ?? []).filter((x) => x !== g.guardrailId)
                      : [...(node.guardrails ?? []), g.guardrailId],
                  })
                }
                className={`flex items-center justify-between rounded-lg border px-2.5 py-1.5 text-left text-[11px] transition-colors ${
                  on ? 'border-amber-300 bg-amber-50 text-amber-700' : 'border-neutral-300 bg-white text-neutral-600'
                }`}
              >
                <span>{g.name}</span>
                <Badge variant="neutral" size="sm">{g.kind}</Badge>
              </button>
            );
          })}
          {guardrails.length === 0 && <span className="text-[11px] text-neutral-400">No guardrails available.</span>}
        </div>
      </Field>

      <Field label="Human review (HITL checkpoint)">
        <button
          onClick={() => onChange({ requiresHitl: !node.requiresHitl })}
          className={`flex w-full items-center justify-between rounded-lg border px-2.5 py-2 text-left text-[12px] transition-colors ${
            node.requiresHitl
              ? 'border-amber-300 bg-amber-50 text-amber-700'
              : 'border-neutral-300 bg-white text-neutral-600'
          }`}
          aria-pressed={!!node.requiresHitl}
          aria-label="Toggle HITL checkpoint"
        >
          <span>{node.requiresHitl ? '⚑ Pauses for approve/reject' : 'Runs without a pause'}</span>
          <span
            className={`relative inline-block h-4 w-7 rounded-full transition-colors ${
              node.requiresHitl ? 'bg-amber-500' : 'bg-neutral-300'
            }`}
          >
            <span
              className={`absolute top-0.5 h-3 w-3 rounded-full bg-white transition-all ${
                node.requiresHitl ? 'left-3.5' : 'left-0.5'
              }`}
            />
          </span>
        </button>
      </Field>

      {node.role === 'validator' && (
        <Field label="Refine loop">
          <div className="flex items-center gap-2 text-[12px] text-neutral-700">
            <input
              value={node.refineTo ?? ''}
              onChange={(e) => onChange({ refineTo: e.target.value || null })}
              placeholder="refine target node id"
              className="flex-1 rounded-lg border border-neutral-300 px-2 py-1 text-[12px]"
              aria-label="Refine target"
            />
            <input
              type="number"
              min={1}
              max={5}
              value={node.maxIterations ?? 3}
              onChange={(e) => onChange({ maxIterations: Number(e.target.value) })}
              className="w-16 rounded-lg border border-neutral-300 px-2 py-1 text-[12px]"
              aria-label="Max iterations"
            />
          </div>
        </Field>
      )}

      <button
        onClick={onDelete}
        className="mt-2 flex items-center justify-center gap-1 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[12px] text-red-600 transition-colors hover:bg-red-100"
      >
        <Trash2 className="h-3.5 w-3.5" /> Delete node
      </button>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="mb-3.5">
      <label className="mb-1.5 block text-[11px] uppercase tracking-wide text-neutral-500">{label}</label>
      {children}
    </div>
  );
}

export default BuildView;
